/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { Table } from '@dataplace.ai/ui-components/organisms'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { IChainBenchmarkData } from '../@types/IChainBenchmark'
import { ANALYSED_VARIABLES_TABLE_ROW_TEMPLATE } from '../utils/constants'
import useGenerateAnalysedVariablesContentRow from '../utils/hooks/useGenerateAnalysedVariablesContentRow'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.5rem;
`

const Description = styled.p(({
  theme: {
    palette, typography,
  },
}) => css`
  font-weight: ${typography.main.pt_15_semibold.fontWeight};
  font-size: ${typography.main.pt_15_semibold.fontSize};
  line-height: ${typography.main.pt_15_semibold.lineHeight};
  letter-spacing: ${typography.main.pt_15_semibold.letterSpacing};
  text-transform: ${typography.main.pt_15_semibold_upper.textTransform};
  color: ${palette.black};
`)

function AnalysedVariables({ valuesData }: Pick<IChainBenchmarkData['value']['benchmark'], 'valuesData'>) {
  const { t } = useTranslation()
  const { generateAnalysedVariablesContentRow } = useGenerateAnalysedVariablesContentRow()

  const tableContent = useMemo(() => [
    generateAnalysedVariablesContentRow({
      averageChainValue: valuesData.population.totalNumber1Km.averageChainValue,
      deviationFromAverage: valuesData.population.totalNumber1Km.deviationFromAverage,
      value: valuesData.population.totalNumber1Km.value,
      variable: t('placeme.chain_benchmark.analysed_variables.table_row.totalNumber1Km'),
    }),
    generateAnalysedVariablesContentRow({
      averageChainValue: valuesData.population['60Share1Km'].averageChainValue,
      deviationFromAverage: valuesData.population['60Share1Km'].deviationFromAverage,
      value: valuesData.population['60Share1Km'].value,
      variable: t('placeme.chain_benchmark.analysed_variables.table_row.60Share1Km'),
    }),
    generateAnalysedVariablesContentRow({
      averageChainValue: valuesData.population.under18Share1Km.averageChainValue,
      deviationFromAverage: valuesData.population.under18Share1Km.deviationFromAverage,
      value: valuesData.population.under18Share1Km.value,
      variable: t('placeme.chain_benchmark.analysed_variables.table_row.under18Share1Km'),
    }),
    generateAnalysedVariablesContentRow({
      averageChainValue: valuesData.population.earnings1Km.averageChainValue,
      deviationFromAverage: valuesData.population.earnings1Km.deviationFromAverage,
      value: valuesData.population.earnings1Km.value,
      variable: t('placeme.chain_benchmark.analysed_variables.table_row.earnings1Km'),
    }),
    generateAnalysedVariablesContentRow({
      averageChainValue: valuesData.traffic.averageDailyTraffic250M.averageChainValue,
      deviationFromAverage: valuesData.traffic.averageDailyTraffic250M.deviationFromAverage,
      value: valuesData.traffic.averageDailyTraffic250M.value,
      variable: t('placeme.chain_benchmark.analysed_variables.table_row.averageDailyTraffic250M'),
    }),
    generateAnalysedVariablesContentRow({
      averageChainValue: valuesData.traffic.morningTrafficShare250M.averageChainValue,
      deviationFromAverage: valuesData.traffic.morningTrafficShare250M.deviationFromAverage,
      value: valuesData.traffic.morningTrafficShare250M.value,
      variable: t('placeme.chain_benchmark.analysed_variables.table_row.morningTrafficShare250M'),
    }),
    generateAnalysedVariablesContentRow({
      averageChainValue: valuesData.traffic.eveningTrafficShare250M.averageChainValue,
      deviationFromAverage: valuesData.traffic.eveningTrafficShare250M.deviationFromAverage,
      value: valuesData.traffic.eveningTrafficShare250M.value,
      variable: t('placeme.chain_benchmark.analysed_variables.table_row.eveningTrafficShare250M'),
    }),
  ], [valuesData])

  return (
    <Wrapper>
      <Description>
        {t('placeme.chain_benchmark.analysed_variables.description')}
      </Description>
      <Table
        buildings
        content={tableContent}
        gap='1rem'
        headerTemplate={ANALYSED_VARIABLES_TABLE_ROW_TEMPLATE}
        labels={[
          t('placeme.chain_benchmark.analysed_variables.table_header.variable'),
          t('placeme.chain_benchmark.analysed_variables.table_header.value'),
          t('placeme.chain_benchmark.analysed_variables.table_header.deviation_from_average'),
          t('placeme.chain_benchmark.analysed_variables.table_header.average_chain_value'),
        ]}
        margin='15px 0 0 0'
        rowTemplate={ANALYSED_VARIABLES_TABLE_ROW_TEMPLATE}
        textLeftIndex={2}
      />
    </Wrapper>
  )
}

export default AnalysedVariables
